import { Box, Flex, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import axios from 'axios';
import { InvoiceActionBar, InvoiceHeader, InvoiceImagePanel, InvoiceRightPanel } from '../../../components/invoice';
import type { InvoiceDetailPageModel } from './InvoiceDetailModals';
import { InvoiceDetailModals } from './InvoiceDetailModals';
import InvoiceActivityLogDrawer from '../../../modals/invoice/InvoiceActivityLogDrawer';
import InvoiceExtractionHintsModal from '../../../modals/invoice/InvoiceExtractionHintsModal';
import InvoiceSubmittedIdentifierCorrectionModal from '../../../modals/invoice/InvoiceSubmittedIdentifierCorrectionModal';
import { useStartInvoiceNegotiation } from '../../../hooks/invoice/useInvoiceNegotiation';
import { invoicesListHref } from '../../../shared/invoicesListViewState';

/** Main invoice detail layout: modals + resizable image / data panels. */
export function InvoiceDetailView({ page }: { page: InvoiceDetailPageModel }) {
    const {
        id,
        invoice,
        state,
        pageReplace,
        toast,
        dataUpdatedAt,
        splitPaneRef,
        leftPanelWidthPercent,
        isDraggingSplit,
        setIsDraggingSplit,
        invoiceImageHighlight,
        setInvoiceImageHighlight,
        pdfPageCount,
        setPdfPageCount,
        isImageAdjustPanelExpanded,
        setIsImageAdjustPanelExpanded,
        setReprocessPagesModalOpen,
        alwaysShowFreightTab,
        salesTaxAsLineItem,
        mismatchFlagFamilyPolicy,
        nexLineQuantityBasis,
        nexReconciliationIdentifierMode,
        isReadOnly,
        isPdf,
        allImages,
        currentImageSrc,
        imageAddAccept,
        submitGating,
        handleSubmitWithArithmeticGuard,
        handleInvoiceTypeChangeRequest,
        handleShopLocationChangeRequest,
        derivedReasonFlags,
        derivedReasonCodes,
        nexSubmissionErrorDetail,
        hasReconciliationIssues,
        hasReconciliationsContent,
        setIsArithmeticBlockOpen,
        isActivityLogOpen,
        setIsActivityLogOpen,
        handleToggleArchive,
        isArchiving,
        extractionTeaching,
        isTeachingOpen,
        openExtractionTeaching,
        handleKeyIdentifierCorrectionCommitted,
        availableTeachFields,
        submittedIdentifierCorrection,
        openSubmittedIdentifierCorrection,
        closeSubmittedIdentifierCorrection,
        handleConfirmSubmittedIdentifierCorrection,
        rememberObservationPending,
    } = page;

    const navigate = useNavigate();
    const localToast = useToast();
    const startNegotiation = useStartInvoiceNegotiation();

    const openNegotiationId = invoice?.openNegotiation?.negotiationId ?? null;

    const isSubmittedStatus =
        invoice?.status === 'submitted' || invoice?.status === 'manually_submitted';

    const showViewNegotiation = isSubmittedStatus && !!openNegotiationId;
    const showRequestCredit =
        isSubmittedStatus && invoice?.negotiationEligible === true && !openNegotiationId;

    const goToNegotiation = useCallback(
        (negotiationId: string) => {
            navigate(
                invoicesListHref({
                    type: 'all',
                    showArchived: false,
                    showNegotiation: true,
                    selectedNegotiationId: negotiationId,
                    currentPage: 1,
                }),
            );
        },
        [navigate],
    );

    const handleViewNegotiation = useCallback(() => {
        if (openNegotiationId) goToNegotiation(openNegotiationId);
    }, [goToNegotiation, openNegotiationId]);

    const handleRequestCredit = useCallback(() => {
        if (!id) return;
        startNegotiation.mutate(id, {
            onSuccess: (data) => {
                localToast({
                    status: 'success',
                    title: 'Negotiation started',
                    description: data.overridden
                        ? `Email sent to override address ${data.to}`
                        : `Email sent to ${data.to}`,
                    duration: 3000,
                    isClosable: true,
                });
                goToNegotiation(data.negotiationId);
            },
            onError: (err: unknown) => {
                if (axios.isAxiosError(err) && err.response?.status === 409) {
                    const negotiationId = (err.response.data as { negotiationId?: string } | undefined)
                        ?.negotiationId;
                    if (negotiationId) {
                        goToNegotiation(negotiationId);
                        return;
                    }
                }
                const message = axios.isAxiosError(err)
                    ? (err.response?.data as { error?: string } | undefined)?.error
                    : undefined;
                localToast({
                    status: 'error',
                    title: 'Could not start negotiation',
                    description: message || 'Please try again.',
                    duration: 4000,
                    isClosable: true,
                });
            },
        });
    }, [goToNegotiation, id, localToast, startNegotiation]);

    const {
        duplicate406BlocksSubmit,
        returnSlipSubmitBlocked,
        locationBlocksSubmit,
        nexMatchBlocksSubmit,
        arithmeticBlocksSubmit,
        submitDisabledHelpCombined,
    } = submitGating;

    return (
<Flex w="100%" h="calc(100vh - 96px)" direction="column" overflow="hidden" border="1px solid" borderColor="gray.200" borderRadius="lg">
            <InvoiceDetailModals page={page} />
            <Flex
                ref={splitPaneRef}
                flex="1"
                overflow="hidden"
                cursor={isDraggingSplit ? 'col-resize' : 'default'}
                userSelect={isDraggingSplit ? 'none' : 'auto'}
            >
                <InvoiceImagePanel
                    key={`invoice-media-${id ?? 'unknown'}-${dataUpdatedAt}`}
                    panelWidthPercent={leftPanelWidthPercent}
                    currentImageSrc={currentImageSrc}
                    isPdf={!!isPdf}
                    allImages={allImages}
                    currentImagePage={state.currentImagePage}
                    onPageChange={state.setCurrentImagePage}
                    zoomImageRef={state.zoomImageRef}
                    invoiceNumber={invoice?.invoiceNumber}
                    imageHighlight={invoiceImageHighlight.region}
                    imageHighlightFullWidth={invoiceImageHighlight.fullWidth}
                    teachingOpen={isTeachingOpen}
                    onOpenTeaching={isTeachingOpen ? extractionTeaching.onBack : openExtractionTeaching}
                    onPdfPageCountChange={setPdfPageCount}
                    pdfPageCount={pdfPageCount}
                    onZoomOut={() => state.zoomImageRef.current?.zoomOut()}
                    onZoomIn={() => state.zoomImageRef.current?.zoomIn()}
                    onZoomReset={() => state.zoomImageRef.current?.reset()}
                    onRotate={() => state.zoomImageRef.current?.rotate()}
                    pageEditActions={{
                        visible: !!(!isReadOnly && pageReplace.editMode),
                        hasPages: pageReplace.hasPages,
                        applyLoading: pageReplace.replaceMutation.isPending,
                        onAdjustImages: () => setIsImageAdjustPanelExpanded(true),
                        onResetOrder: pageReplace.cancelEditMode,
                        onRemoveAll: pageReplace.startOver,
                        onReprocess: () => setReprocessPagesModalOpen(true),
                    }}
                    pageReplacement={{
                        show: !isReadOnly,
                        editMode: pageReplace.editMode,
                        draftBootstrapping: pageReplace.draftBootstrapping,
                        isPdf: !!isPdf,
                        pdfThumbsLoading: pageReplace.pdfThumbsLoading,
                        thumbnailUrls: pageReplace.stripDisplayUrls,
                        sortableIds: pageReplace.stripDisplayIds,
                        selectedIndex0: state.currentImagePage - 1,
                        onSelectIndex: (i) => state.setCurrentImagePage(i + 1),
                        onReorder: (from, to) => {
                            if (isPdf) void pageReplace.reorderPdf(from, to);
                            else pageReplace.reorderImages(from, to);
                        },
                        onRemoveAt: (i) => {
                            if (isPdf) void pageReplace.removePdfAt(i);
                            else pageReplace.removeImageAt(i);
                        },
                        onAddImages: (files) => {
                            const result = pageReplace.addRasterPages(files);
                            if (result.ok) {
                                state.setCurrentImagePage(result.newTotal);
                                return;
                            }
                            toast({
                                title: 'Could not add image',
                                description: result.reason,
                                status: 'warning',
                                duration: 3500,
                                isClosable: true,
                            });
                        },
                        imageAddAccept,
                        onAddPdf: (f) => void pageReplace.appendPdfPages(f),
                        onReprocess: () => setReprocessPagesModalOpen(true),
                        applyLoading: pageReplace.replaceMutation.isPending,
                        error: pageReplace.error,
                        hasPages: pageReplace.hasPages,
                        onCancel: pageReplace.cancelEditMode,
                        onStartOver: pageReplace.startOver,
                        panelExpanded: isImageAdjustPanelExpanded,
                        onSetPanelExpanded: setIsImageAdjustPanelExpanded,
                    }}
                />
                <Box
                    role="separator"
                    aria-label="Resize invoice image and details panels"
                    aria-orientation="vertical"
                    w="10px"
                    flexShrink={0}
                    cursor="col-resize"
                    bg={isDraggingSplit ? 'blue.100' : 'transparent'}
                    borderLeft="1px solid"
                    borderRight="1px solid"
                    borderColor={isDraggingSplit ? 'blue.300' : 'gray.200'}
                    transition="background-color 0.12s ease"
                    _hover={{ bg: 'gray.100' }}
                    onMouseDown={(evt) => {
                        evt.preventDefault();
                        setIsDraggingSplit(true);
                    }}
                />
                <Flex direction="column" flex="1" minW={0} overflow="hidden" position="relative">
                    <Flex
                        direction="column"
                        flex="1"
                        minH={0}
                        overflow="hidden"
                        aria-hidden={isTeachingOpen}
                    >
                    <InvoiceHeader
                        invoiceId={id}
                        status={invoice?.status}
                        submittedByName={invoice?.submittedByName}
                        activeTab={state.activeRhsTab}
                        onTabChange={state.setActiveRhsTab}
                        hasReconciliationIssues={hasReconciliationIssues}
                        hasReconciliationsContent={hasReconciliationsContent}
                        nexSubmissionErrorDetail={nexSubmissionErrorDetail}
                        reasonFlags={derivedReasonFlags}
                        reasonCodes={derivedReasonCodes}
                        nexReconciliationIdentifierMode={nexReconciliationIdentifierMode}
                        onOpenActivityLog={() => setIsActivityLogOpen(true)}
                        listNavigation={page.listNavigation}
                    />
                    <Box flex="1" minH={0} overflow="hidden">
                <InvoiceRightPanel
                    panelWidthPercent={100}
                    detailInvoiceId={id ?? ''}
                    rememberedKeyIdentifiersOpen={page.rememberedKeyIdentifiersOpen}
                    onRememberKeyIdentifiersOpen={page.setRememberedKeyIdentifiersOpen}
                    invoice={invoice!}
                    editedFields={state.editedFields}
                    activeRhsTab={state.activeRhsTab}
                    hasReconciliationIssues={hasReconciliationIssues}
                    isEditingInvoiceNumber={state.isEditingInvoiceNumber}
                    setIsEditingInvoiceNumber={state.setIsEditingInvoiceNumber}
                    invoiceInputRef={state.invoiceInputRef}
                    lineItemsTabIndex={state.lineItemsTabIndex}
                    setLineItemsTabIndex={state.setLineItemsTabIndex}
                    lineItemsTotal={state.lineItemsTotal}
                    freightTotal={state.freightTotal}
                    fuelItemsTotal={state.fuelItemsTotal}
                    restockingFeeTotal={state.restockingFeeTotal}
                    discountTotal={state.discountTotal}
                    lineItemBackendIndexByCurrentIndex={state.lineItemBackendIndexByCurrentIndex}
                    unmatchedPartIndices={state.unmatchedPartIndices}
                    localMatchedNexOverrides={state.localMatchedNexOverrides}
                    flaggedPartIndices={state.flaggedPartIndices}
                    expandedMatchedNexRows={state.expandedMatchedNexRows}
                    setExpandedMatchedNexRows={state.setExpandedMatchedNexRows}
                    expandedMatchedNexIgnoredRows={state.expandedMatchedNexIgnoredRows}
                    setExpandedMatchedNexIgnoredRows={state.setExpandedMatchedNexIgnoredRows}
                    setUnmatchConfirmModal={state.setUnmatchConfirmModal}
                    aboveLineItemsSubtotal={state.aboveLineItemsSubtotal}
                    editingNumericField={state.editingNumericField}
                    editingNumericValues={state.editingNumericValues}
                    setEditingNumericField={state.setEditingNumericField}
                    setEditingNumericValues={state.setEditingNumericValues}
                    updateField={state.updateField}
                    updateLineItem={state.updateLineItem}
                    restoreLineItemRowsSnapshot={state.restoreLineItemRowsSnapshot}
                    addLineItem={state.addLineItem}
                    addLineItemAbove={state.addLineItemAbove}
                    addLineItemAt={state.addLineItemAt}
                    duplicateLineItemAt={state.duplicateLineItemAt}
                    removeLineItem={state.removeLineItem}
                    reorderLineItems={state.reorderLineItems}
                    reorderFreightItems={state.reorderFreightItems}
                    reorderRestockingFeeItems={state.reorderRestockingFeeItems}
                    reorderIgnoredParts={state.reorderIgnoredParts}
                    addFreightItem={state.addFreightItem}
                    addFreightItemAt={state.addFreightItemAt}
                    addFreightItemAbove={state.addFreightItemAbove}
                    duplicateFreightItemAt={state.duplicateFreightItemAt}
                    removeFreightItem={state.removeFreightItem}
                    updateFreightItem={state.updateFreightItem}
                    addFuelItem={state.addFuelItem}
                    addFuelItemAt={state.addFuelItemAt}
                    removeFuelItem={state.removeFuelItem}
                    updateFuelItem={state.updateFuelItem}
                    addRestockingFeeItem={state.addRestockingFeeItem}
                    addRestockingFeeItemAt={state.addRestockingFeeItemAt}
                    addRestockingFeeItemAbove={state.addRestockingFeeItemAbove}
                    duplicateRestockingFeeItemAt={state.duplicateRestockingFeeItemAt}
                    removeRestockingFeeItem={state.removeRestockingFeeItem}
                    updateRestockingFeeItem={state.updateRestockingFeeItem}
                    restoreIgnoredPart={state.restoreIgnoredPart}
                    openRevertModal={state.openRevertModal}
                    openReconciliationDetailModal={state.openReconciliationDetailModal}
                    getStringValue={state.getStringValue}
                    getDateValue={state.getDateValue}
                    getNumberValue={state.getNumberValue}
                    isFieldEdited={state.isFieldEdited}
                    buildTabData={state.buildTabData}
                    getLineItemStringValue={state.getLineItemStringValue}
                    isLineItemFieldEdited={state.isLineItemFieldEdited}
                    hasAnyLineItemFieldEdited={state.hasAnyLineItemFieldEdited}
                    getLineItemEditedFieldLabels={state.getLineItemEditedFieldLabels}
                    getIssuesForPart={state.getIssuesForPart}
                    getLineItemExpandKey={state.getLineItemExpandKey}
                    getIssueTooltip={state.getIssueTooltip}
                    getPartNumberChoiceIssue={state.getPartNumberChoiceIssue}
                    getMissingInNexSuggestion={state.getMissingInNexSuggestion}
                    getFreightItemStringValue={state.getFreightItemStringValue}
                    getFreightItemPriceValue={state.getFreightItemPriceValue}
                    getFuelItemStringValue={state.getFuelItemStringValue}
                    getFuelItemPriceValue={state.getFuelItemPriceValue}
                    getRestockingFeeItemStringValue={state.getRestockingFeeItemStringValue}
                    getRestockingFeeItemPriceValue={state.getRestockingFeeItemPriceValue}
                    renderLineItemNumericCell={state.renderLineItemNumericCell}
                    renderNumericField={state.renderNumericField}
                    getIssueTooltipText={state.getIssueTooltipText}
                    fmtUsd={state.fmtUsd}
                    renderFreightItemNumericCell={state.renderFreightItemNumericCell}
                    renderFuelItemNumericCell={state.renderFuelItemNumericCell}
                    renderRestockingFeeItemNumericCell={state.renderRestockingFeeItemNumericCell}
                    addIgnoredPart={state.addIgnoredPart}
                    addIgnoredPartAt={state.addIgnoredPartAt}
                    addIgnoredPartAbove={state.addIgnoredPartAbove}
                    removeIgnoredPart={state.removeIgnoredPart}
                    updateIgnoredPart={state.updateIgnoredPart}
                    getIgnoredPartStringValue={state.getIgnoredPartStringValue}
                    getIgnoredPartPriceValue={state.getIgnoredPartPriceValue}
                    renderIgnoredPartNumericCell={state.renderIgnoredPartNumericCell}
                    effectiveMatchedNexLines={state.effectiveMatchedNexLines}
                    effectiveReconciliationMatchNotes={state.effectiveReconciliationMatchNotes}
                    isReadOnly={isReadOnly}
                    localGrandTotalVsAboveLineItemsWithFeesMismatch={state.localGrandTotalVsAboveLineItemsWithFeesMismatch ?? null}
                    alwaysShowFreightTab={alwaysShowFreightTab}
                    freightAsLineItem={alwaysShowFreightTab}
                    salesTaxAsLineItem={salesTaxAsLineItem}
                    mismatchFlagFamilyPolicy={mismatchFlagFamilyPolicy}
                    nexLineQuantityBasis={nexLineQuantityBasis}
                    creditedTotalsLayout={state.isCurrentReturnInvoice}
                    onInvoiceTypeChangeRequest={!isReadOnly ? handleInvoiceTypeChangeRequest : undefined}
                    onShopLocationChangeRequest={!isReadOnly ? handleShopLocationChangeRequest : undefined}
                    onRefreshNexsyisData={!isReadOnly ? state.handleRefreshNexsyisData : undefined}
                    isRefreshNexsyisPending={state.updateInvoiceMutation.isPending || state.invoiceUpdateInProgress}
                    onLineItemImageHighlightChange={
                        (region) => {
                            if (region?.page && region.page !== state.currentImagePage) {
                                state.setCurrentImagePage(region.page);
                            }
                            setInvoiceImageHighlight({ region, fullWidth: !!region });
                        }
                    }
                    onFieldImageHighlightChange={
                        (region) => {
                            if (region?.page && region.page !== state.currentImagePage) {
                                state.setCurrentImagePage(region.page);
                            }
                            setInvoiceImageHighlight({ region, fullWidth: false });
                        }
                    }
                    nexReconciliationIdentifierMode={nexReconciliationIdentifierMode}
                    nexTaxFormat={state.nexTaxFormat}
                    onNexTaxFormatChange={state.handleNexTaxFormatChange}
                    showCanadianTaxControls={state.showCanadianTaxControls}
                    onOpenExtractionTeaching={
                        !isReadOnly ? openExtractionTeaching : undefined
                    }
                    onKeyIdentifierCorrectionCommitted={
                        !isReadOnly ? handleKeyIdentifierCorrectionCommitted : undefined
                    }
                    onReportSubmittedIdentifier={openSubmittedIdentifierCorrection}
                    teachableFields={availableTeachFields}
                />
                    </Box>
                    <InvoiceActionBar
                        archived={!!invoice?.archived}
                        onSubmit={() =>
                            handleSubmitWithArithmeticGuard(
                                () => setIsArithmeticBlockOpen(true),
                                () => state.handleSubmitClick(),
                            )
                        }
                        isSubmitting={state.updateInvoiceMutation.isPending || state.invoiceUpdateInProgress}
                        disableSubmit={
                            duplicate406BlocksSubmit ||
                            returnSlipSubmitBlocked ||
                            locationBlocksSubmit ||
                            nexMatchBlocksSubmit ||
                            arithmeticBlocksSubmit ||
                            pageReplace.draftBootstrapping ||
                            pageReplace.blocksNexSubmit ||
                            pageReplace.replaceMutation.isPending ||
                            state.invoiceUpdateInProgress
                        }
                        submitDisabledHelpText={
                            state.invoiceUpdateInProgress
                                ? 'This invoice is still updating. You can leave this page.'
                                : pageReplace.replaceMutation.isPending
                                ? 'Re-processing invoice pages…'
                                : pageReplace.draftBootstrapping
                                    ? 'Loading invoice pages…'
                                    : pageReplace.blocksNexSubmit
                                        ? pageReplace.blocksNexSubmitReason
                                        : submitDisabledHelpCombined
                        }
                        isReadOnly={isReadOnly}
                        onToggleArchive={() => void handleToggleArchive()}
                        isArchiving={isArchiving}
                        showRequestCredit={showRequestCredit}
                        showViewNegotiation={showViewNegotiation}
                        onRequestCredit={handleRequestCredit}
                        onViewNegotiation={handleViewNegotiation}
                        isStartingNegotiation={startNegotiation.isPending}
                    />
                    </Flex>
                    <InvoiceExtractionHintsModal isOpen={isTeachingOpen} {...extractionTeaching} />
                    <InvoiceSubmittedIdentifierCorrectionModal
                        request={submittedIdentifierCorrection?.request ?? null}
                        vendorName={extractionTeaching.vendorName}
                        invoiceTypeLabel={extractionTeaching.invoiceTypeLabel}
                        isLoading={rememberObservationPending}
                        error={submittedIdentifierCorrection?.error}
                        onClose={closeSubmittedIdentifierCorrection}
                        onConfirm={handleConfirmSubmittedIdentifierCorrection}
                    />
                </Flex>
            </Flex>
            {id && (
                <InvoiceActivityLogDrawer
                    isOpen={isActivityLogOpen}
                    onClose={() => setIsActivityLogOpen(false)}
                    invoiceId={id}
                    hideOverlay
                />
            )}
        </Flex>
    );
}
